import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './JobProgressPopup.css';
import { makeRequest, handleDownloadFile } from '@utils/helpers';
import { Empty, message, Popconfirm, Progress, Spin } from 'antd';
import { getReportsFromJobsReq, downloadReportFromJobsReq } from '../../modules/reports/allReports/requests';
import { BellOutlined, CloseOutlined, DownloadOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined, LoadingOutlined } from '@ant-design/icons';
import { decryptLocalData, pusherInitializer } from '@comps/commonFunctions';
import { useSelector } from 'react-redux';
import { cancelPayrollJobReq, getBatchProgressReq } from '../../modules/common/requests';
import { convertTimeTotz } from '../commonFunctions';

const getBatchItems = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.batches)) {
    return data.batches;
  }

  return [];
};

const hasActiveBatchJobs = (items = []) => items.some((item) => Number(item?.progress ?? 0) < 100);

const JobProgressPopup = ({ standalone = false }) => {
  const authUser = useSelector((state) => state.auth.authUser);
  const selectedNotificationRef = useRef(null);
  const popupRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [jobBatches, setJobBatches] = useState([]);

  const totalItems = dataSource.length + jobBatches.length;
  const hasActiveJobs = useMemo(() => {
    const hasActiveReports = dataSource.some((item) => !item?.is_downloaded || Number(item?.progress ?? 0) < 100);
    return hasActiveReports || hasActiveBatchJobs(jobBatches);
  }, [dataSource, jobBatches]);

  const onSuccessReports = useCallback((data) => {
    setDataSource(Array.isArray(data?.jobs) ? data.jobs : []);
  }, []);

  const onSuccessBatchProgress = useCallback((data) => {
    setJobBatches(getBatchItems(data));
  }, []);

  const onErrorReports = useCallback((err) => {
    console.log(err);
  }, []);

  const getJobStatus = useCallback(() => {
    makeRequest(setLoader, getReportsFromJobsReq, null, onSuccessReports, onErrorReports);
    // makeRequest(setLoader, getBatchProgressReq, null, onSuccessBatchProgress, onErrorReports);
  }, [onErrorReports, onSuccessBatchProgress, onSuccessReports]);

  useEffect(() => {
    const user = decryptLocalData('user');
    if (!user || !user.tenant) {
      return undefined;
    }

    const pusher = pusherInitializer();
    const channel = pusher.subscribe(user.tenant);
    const handleRealtimeUpdate = () => {
      getJobStatus();
      setIsOpen(true);
    };

    channel.bind('report-downloaded-event', handleRealtimeUpdate);
    channel.bind('batch-progress-event', handleRealtimeUpdate);

    return () => {
      channel.unbind('report-downloaded-event', handleRealtimeUpdate);
      channel.unbind('batch-progress-event', handleRealtimeUpdate);
      pusher.unsubscribe(user.tenant);
      pusher.disconnect();
    };
  }, [authUser, getJobStatus]);

  useEffect(() => {
    getJobStatus();

    const timer = setInterval(() => {
      if (hasActiveJobs) {
        getJobStatus();
      }
    }, 5000);

    return () => clearInterval(timer);
  }, [getJobStatus, hasActiveJobs]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const onView = (item) => {
    selectedNotificationRef.current = item;
    makeRequest(setLoader, downloadReportFromJobsReq, item.id, onSuccessView, onErrorView);
  };

  const onSuccessView = (err, res) => {
    const selectedNotification = selectedNotificationRef.current;

    if (res.code === undefined && selectedNotification) {
      const fileExtension = res.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ? 'xlsx' : 'pdf';
      handleDownloadFile(`${selectedNotification.title}.${fileExtension}`, res);
      return;
    }

    message.error(res.message);
  };

  const onErrorView = (err, res) => {
    const selectedNotification = selectedNotificationRef.current;

    if (res.code === undefined && selectedNotification) {
      const fileExtension = res.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ? 'xlsx' : 'pdf';
      handleDownloadFile(`${selectedNotification.title}.${fileExtension}`, res);
      return;
    }

    message.error(res.message);
  };

  const onCancelBatch = (item) => {
    const payrollId = item?.options?.data?.param?.payroll_id;

    if (payrollId) {
      makeRequest(setLoader, cancelPayrollJobReq, payrollId, getJobStatus, null);
    }
  };

  const getItemTitle = (item) => item?.title || item?.name || item?.options?.data?.class?.split('\\').pop() || 'Background job';

  const getItemTime = (item) => {
    const timestamp = item?.created_at || item?.updated_at;
    return timestamp ? convertTimeTotz(timestamp).format('YYYY-MM-DD hh:mm a') : 'In progress';
  };

  const renderReportStatus = (item) => {
    if (item?.is_downloaded) {
      return (
        <button type="button" className="job-progress-popup__icon-button is-success" onClick={ () => onView(item) }>
          <DownloadOutlined />
        </button>
      );
    }

    if (Number(item?.progress) === 100) {
      return <Spin indicator={ <LoadingOutlined spin /> } />;
    }

    return <Progress type="circle" percent={ Number(item?.progress ?? 0) } size={ 34 } strokeColor="#2F6BFF" />;
  };

  const renderBatchStatus = (item) => {
    const payrollJob = item?.options?.data?.class === 'App\\Jobs\\PayrollManagement\\RunPayroll';

    return (
      <div className="job-progress-popup__batch-actions">
        <Progress type="circle" percent={ Number(item?.progress ?? 0) } size={ 34 } strokeColor="#15B79E" />
        { payrollJob && (
          <Popconfirm title="Are you sure to cancel this task?" onConfirm={ () => onCancelBatch(item) }>
            <button type="button" className="job-progress-popup__icon-button is-danger">
              <CloseOutlined />
            </button>
          </Popconfirm>
        ) }
      </div>
    );
  };

  const notificationItems = useMemo(() => {
    const reportItems = dataSource.map((item) => ({
      key: `report-${item.id}`,
      item,
      type: 'report',
    }));

    const batchItems = jobBatches.map((item, index) => ({
      key: `batch-${item.id || index}`,
      item,
      type: 'batch',
    }));

    return [...batchItems, ...reportItems];
  }, [dataSource, jobBatches]);

  const onTogglePopup = () => {
    getJobStatus();
    setIsOpen((current) => !current);
  };

  return (
    <div
      ref={ popupRef }
      className={ `job-progress-popup${standalone ? ' is-standalone' : ''}${isOpen ? ' is-open' : ''}` }
    >
      <button
        type="button"
        className={ `job-progress-popup__trigger${hasActiveJobs ? ' has-activity' : ''}` }
        onClick={ onTogglePopup }
        aria-label="Toggle job progress"
      >
        <BellOutlined />
        { totalItems > 0 && <span className="job-progress-popup__badge">{ totalItems }</span> }
      </button>

      { isOpen && (
        <div className="job-progress-popup__panel">
          <div className="job-progress-popup__panel-header">
            <div>
              <div className="job-progress-popup__eyebrow">Background jobs</div>
              <div className="job-progress-popup__title">Progress center</div>
            </div>
            <button
              type="button"
              className="job-progress-popup__close-button"
              onClick={ () => setIsOpen(false) }
              aria-label="Close job progress"
            >
              <CloseOutlined />
            </button>
          </div>

          <div className="job-progress-popup__panel-summary">
            <span>{ totalItems } item{ totalItems === 1 ? '' : 's' }</span>
            <span>{ hasActiveJobs ? 'Live updates on' : 'All caught up' }</span>
          </div>

          <div className="job-progress-popup__list">
            { !notificationItems.length && (
              <div className="job-progress-popup__empty">
                <Empty
                  image={ Empty.PRESENTED_IMAGE_SIMPLE }
                  description="No job progress to show right now."
                />
              </div>
            ) }

            { notificationItems.map(({ key, item, type }) => (
              <div className="job-progress-popup__item" key={ key }>
                <div className="job-progress-popup__item-icon">

                  { !item.path ? '' : item.path.includes('.xlsx') ? <FileExcelOutlined className='text-success' /> : <FilePdfOutlined className='text-danger' />
                  }
                </div>

                <div className="job-progress-popup__item-content">
                  <div className="job-progress-popup__item-title">{ getItemTitle(item) }</div>
                  <div className="job-progress-popup__item-meta">
                    {/* <span>{ type === 'batch' ? 'Job' : 'Job' }</span> */ }
                    <span>{ getItemTime(item) }</span>
                  </div>
                </div>

                <div className="job-progress-popup__item-status">
                  { type === 'batch' ? renderBatchStatus(item) : renderReportStatus(item) }
                </div>
              </div>
            )) }
          </div>

          { loader && (
            <div className="job-progress-popup__footer">
              <Spin size="small" />
              <span>Refreshing latest status...</span>
            </div>
          ) }
        </div>
      ) }
    </div>
  );
};

export default JobProgressPopup;
